import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "../axios";
import { removePrompt, useConfirm } from "./ConfirmDialog";
import EntryReceipt from "./EntryReceipt";
import {
  MenuLink,
  freeTicketNote,
  includedSpectatorTickets,
  money,
  showDate,
  StubDate,
  spectatorHolding,
  typeName,
  yearSpan,
} from "./ShowPieces";

const registrationSteps = [
  { id: 1, label: "Your car" },
  { id: 2, label: "Class" },
  { id: 3, label: "Spectators" },
  { id: 4, label: "VIP" },
  { id: 5, label: "Receipt" },
];

function authHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function ticketCount(tickets) {
  return Object.values(tickets || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
}

function dayTicketTitle(value) {
  if (!value) {
    return "Ticket";
  }
  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) {
    return "Ticket";
  }
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  return `${weekday}'s Ticket`;
}

function capVipTickets(tickets, spectatorCount) {
  let room = Math.max(0, Number(spectatorCount) || 0);
  const next = {};
  Object.entries(tickets || {}).forEach(([id, qty]) => {
    const count = Math.max(0, Number(qty) || 0);
    const allowed = Math.min(count, room);
    room -= allowed;
    if (allowed > 0) {
      next[id] = allowed;
    }
  });
  return next;
}

function sameTickets(left, right) {
  const keys = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
  for (const key of keys) {
    if ((Number(left?.[key]) || 0) !== (Number(right?.[key]) || 0)) {
      return false;
    }
  }
  return true;
}

function registrationCleared(nextCars, nextTickets, nextSpectators) {
  return nextCars.length === 0 && ticketCount(nextTickets) < 1 && Number(nextSpectators) < 1;
}

function formWasUsed(saved) {
  if (!saved) {
    return false;
  }
  return (
    ticketCount(saved.tickets) > 0 ||
    Number(saved.spectators) > 0 ||
    (saved.stage || 1) >= 3 ||
    (saved.reached || 1) >= 3
  );
}

function classMakes(item, makes) {
  if (!item?.manufacture_ids?.length) {
    return [];
  }
  const ids = new Set(item.manufacture_ids.map(String));
  return makes.filter((make) => ids.has(String(make.id)));
}

function typeLimit(value) {
  if (value === "all") {
    return "Automobiles and motorcycles";
  }
  if (value === "automobile") {
    return "Automobiles only";
  }
  if (value === "motorcycle") {
    return "Motorcycles only";
  }
  return typeName(value);
}

function yearLimit(item) {
  if (item.year_start && item.year_end) {
    return `${item.year_start} through ${item.year_end}`;
  }
  if (item.year_start) {
    return `${item.year_start} and later`;
  }
  if (item.year_end) {
    return `Through ${item.year_end}`;
  }
  return "Any year";
}

function classLimitSentence(item) {
  const kind = {
    all: "an automobile or a motorcycle",
    automobile: "an automobile",
    motorcycle: "a motorcycle",
  }[item.vehicle_type] || "a vehicle";
  let years = "from any year";
  if (item.year_start && item.year_end) {
    years = `built from ${item.year_start} through ${item.year_end}`;
  } else if (item.year_start) {
    years = `built in ${item.year_start} or later`;
  } else if (item.year_end) {
    years = `built through ${item.year_end}`;
  }
  const makes = item.manufacture_ids?.length
    ? " It has to be one of the makes below."
    : " Any make can enter.";
  return `This class accepts ${kind} ${years}.${makes}`;
}

function classFits(vehicle, item, heldCars) {
  if (!vehicle || !item) {
    return false;
  }
  if (item.vehicle_type !== "all" && item.vehicle_type !== vehicle.vehicle_type) {
    return false;
  }
  if (item.year_start && vehicle.year < item.year_start) {
    return false;
  }
  if (item.year_end && vehicle.year > item.year_end) {
    return false;
  }
  if (item.manufacture_ids?.length && !item.manufacture_ids.includes(vehicle.manufacture)) {
    return false;
  }
  if (item.concours && !vehicle.concours) {
    return false;
  }
  if (item.closed) {
    const kept = (heldCars || []).some(
      (car) =>
        String(car.vehicle) === String(vehicle.id) &&
        String(car.event_car_class) === String(item.id)
    );
    if (!kept) {
      return false;
    }
  }
  return true;
}

function EntryWizard({ event, userInfo, open, onClose, startStage = 1, purpose = "register" }) {
  const navigate = useNavigate();
  const storageKey = `hbc-entry-${userInfo?.id}-${event?.id}`;
  const [stage, setStage] = useState(1);
  const [reached, setReached] = useState(1);
  const [editingVehicle, setEditingVehicle] = useState("");
  const [cars, setCars] = useState([]);
  const [garage, setGarage] = useState([]);
  const [makes, setMakes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [tickets, setTickets] = useState({});
  const [spectators, setSpectators] = useState(0);
  const [vehicleId, setVehicleId] = useState("");
  const [classId, setClassId] = useState("");
  const [division, setDivision] = useState("display");
  const [judging, setJudging] = useState(false);
  const [ask, confirmDialog] = useConfirm();
  const [adding, setAdding] = useState(false);
  const [newCar, setNewCar] = useState({
    manufacture: "",
    vehicle_type: "automobile",
    year: "",
    model: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [classesLoaded, setClassesLoaded] = useState(false);
  const [openClass, setOpenClass] = useState(null);
  const [draftReady, setDraftReady] = useState(false);
  const [seenOpen, setSeenOpen] = useState(open);
  const includedAnchor = useRef(null);
  if (seenOpen !== open) {
    setSeenOpen(open);
    setDraftReady(false);
    setReady(false);
    includedAnchor.current = null;
    if (open) {
      setError("");
      const land = purpose === "vip" ? 3 : startStage > 1 ? startStage : 1;
      setStage(land);
      setReached(land);
      setVehicleId("");
      setClassId("");
      setDivision("display");
      setJudging(false);
      setTickets({});
      setSpectators(0);
      setEditingVehicle("");
      setCars([]);
    }
  }
  const lunch = event?.lunches?.[0];
  const entryOptions = event?.entry_options || [];

  useEffect(() => {
    const options = event?.entry_options || [];
    if (!options.length) {
      return;
    }
    const codes = options.map((item) => item.code);
    if (!codes.includes(division)) {
      setDivision(codes.includes("display") ? "display" : codes[0]);
      return;
    }
    if (division === "display" && judging) {
      setJudging(false);
    }
  }, [event, division, judging]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!lunch && stage === 4) {
      setStage(5);
      return;
    }
    if (stage === 2 && !vehicleId) {
      setStage(1);
    }
    if (lunch && stage === 4 && Number(spectators) < 1) {
      setStage(3);
    }
  }, [open, lunch, stage, vehicleId, spectators]);

  const forgetSavedEntry = () => {
    window.localStorage.removeItem(storageKey);
    includedAnchor.current = 0;
    setStage(1);
    setReached(1);
    setVehicleId("");
    setClassId("");
    setDivision("display");
    setJudging(false);
    setTickets({});
    setSpectators(0);
    setEditingVehicle("");
  };

  useEffect(() => {
    if (!open || !userInfo || !event) {
      return undefined;
    }
    let cancel = false;
    setClassesLoaded(false);
    const openAt = purpose === "vip" ? 3 : startStage > 1 ? startStage : 0;
    let saved = null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }
    const restorePick = (draft) => {
      let seen = Math.max(draft.reached || 1, draft.stage || 1);
      let savedStage = draft.stage || 1;
      if (!draft.flow) {
        if (savedStage >= 3) savedStage += 1;
        if (seen >= 3) seen += 1;
      }
      setStage(openAt || savedStage || 1);
      setReached(openAt ? Math.max(seen, openAt) : seen);
      setVehicleId(draft.vehicleId || "");
      setClassId(draft.classId || "");
      setDivision(draft.division || "display");
      setJudging(Boolean(draft.judging));
      setEditingVehicle(draft.editingVehicle || "");
    };
    axios
      .get(`/api/users/draft/?event=${event.id}`, authHeaders(userInfo.token))
      .then(({ data }) => {
        if (cancel) {
          return;
        }
        const serverTickets = {};
        (data.tickets || []).forEach((item) => {
          serverTickets[item.vip_ticket_type] = item.quantity;
        });
        const serverSpectator = Number(data.spectator) || 0;
        const serverCars = data.cars || [];
        const serverEmpty = registrationCleared(serverCars, serverTickets, serverSpectator);
        setCars(serverCars);
        setClasses(data.classes || []);
        setClassesLoaded(true);
        if (serverEmpty && formWasUsed(saved)) {
          window.localStorage.removeItem(storageKey);
          setStage(openAt || 1);
          setReached(openAt || 1);
          setVehicleId("");
          setClassId("");
          setDivision("display");
          setJudging(false);
          setTickets({});
          setSpectators(0);
          setEditingVehicle("");
        } else if (serverEmpty && saved && (saved.stage || 1) <= 2 && (saved.reached || 1) <= 2) {
          restorePick(saved);
        } else if (!serverEmpty) {
          if (saved) {
            restorePick(saved);
          } else if (openAt) {
            setStage(openAt);
            setReached(openAt);
          }
          setTickets(capVipTickets(serverTickets, serverSpectator));
          setSpectators(serverSpectator);
          if ((data.tickets || []).length > 0 || serverSpectator > 0) {
            setReached((current) => Math.max(current, 5));
          }
        }
        setDraftReady(true);
        setReady(true);
      })
      .catch(() => {
        if (cancel) {
          return;
        }
        setClassesLoaded(true);
        if (saved && !formWasUsed(saved)) {
          restorePick(saved);
        }
        setDraftReady(true);
        setReady(true);
        setError("The saved draft could not be opened.");
      });
    axios
      .get("/api/users/garage/", authHeaders(userInfo.token))
      .then(({ data }) => {
        if (!cancel) {
          setGarage(data);
        }
      })
      .catch(() => {
        if (!cancel) {
          setGarage([]);
        }
      });
    axios
      .get("/api/users/manufactures/", authHeaders(userInfo.token))
      .then(({ data }) => {
        if (!cancel) {
          setMakes(data);
        }
      })
      .catch(() => {
        if (!cancel) {
          setMakes([]);
        }
      });
    return () => {
      cancel = true;
    };
  }, [open, userInfo, event, storageKey, startStage, purpose]);

  useEffect(() => {
    if (!open || !ready) {
      return;
    }
    const seen = Math.max(reached, stage);
    if (seen !== reached) {
      setReached(seen);
    }
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        stage,
        reached: seen,
        vehicleId,
        classId,
        division,
        judging,
        tickets,
        spectators,
        editingVehicle,
        flow: 5,
      })
    );
  }, [open, storageKey, stage, reached, vehicleId, classId, division, judging, tickets, spectators, editingVehicle]);

  useEffect(() => {
    if (!open || !draftReady) {
      return;
    }
    const included = includedSpectatorTickets(cars.length);
    if (includedAnchor.current === null) {
      includedAnchor.current = included;
      setSpectators((current) => Math.max(Number(current) || 0, included));
      return;
    }
    if (includedAnchor.current === included) {
      return;
    }
    const previousIncluded = includedAnchor.current;
    includedAnchor.current = included;
    setSpectators((current) => {
      const paid = Math.max(0, (Number(current) || 0) - previousIncluded);
      return included + paid;
    });
  }, [open, draftReady, cars.length]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTickets((current) => {
      const next = capVipTickets(current, spectators);
      return sameTickets(current, next) ? current : next;
    });
  }, [open, spectators]);

  const vehicle = garage.find((item) => String(item.id) === String(vehicleId));
  const includedTickets = includedSpectatorTickets(cars.length);
  const freeNote = freeTicketNote(cars.length);
  const fitting = classes.filter((item) => classFits(vehicle, item, cars));
  const canJudge = !classesLoaded || fitting.length > 0;

  useEffect(() => {
    if (classesLoaded && fitting.length === 0 && judging) {
      setJudging(false);
    }
  }, [classesLoaded, fitting.length, judging]);

  useEffect(() => {
    if (!classId || !classesLoaded || !vehicle) {
      return;
    }
    const chosen = classes.find((item) => String(item.id) === String(classId));
    if (chosen && !classFits(vehicle, chosen, cars)) {
      setClassId("");
    }
  }, [classId, classes, classesLoaded, vehicle, cars]);

  const saveDraft = async (nextCars, nextTickets, nextSpectators = spectators) => {
    const heldSpectators = Number(nextSpectators) || 0;
    const heldTickets = capVipTickets(nextTickets, heldSpectators);
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.put(
        "/api/users/draft/",
        {
          event: event.id,
          cars: nextCars.map((item) => ({
            vehicle: item.vehicle,
            event_car_class: item.event_car_class,
            division: item.division,
            judging: item.judging,
          })),
          tickets: Object.entries(heldTickets)
            .filter(([, quantity]) => Number(quantity) > 0)
            .map(([id, quantity]) => ({ vip_ticket_type: Number(id), quantity: Number(quantity) })),
          spectator: heldSpectators,
        },
        authHeaders(userInfo.token)
      );
      setCars(data.cars || []);
      setSaving(false);
      window.dispatchEvent(new Event("hbc-checkout"));
      return true;
    } catch (err) {
      setSaving(false);
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.join(" ") : detail || "The draft could not be saved.");
      return false;
    }
  };

  const registeredIds = new Set(cars.map((item) => String(item.vehicle)));
  const carTaken = (id) => {
    const key = String(id);
    if (editingVehicle && key === String(editingVehicle)) {
      return false;
    }
    return registeredIds.has(key) && key !== String(vehicleId);
  };

  const addCurrentCar = async () => {
    if (!vehicle || !classId) {
      setError("Choose a car and a class.");
      return false;
    }
    if (carTaken(vehicle.id)) {
      setError("That car is already on this registration.");
      return false;
    }
    const replaced = editingVehicle ? String(editingVehicle) : "";
    const next = cars.filter((item) => {
      const key = String(item.vehicle);
      return key !== String(vehicle.id) && key !== replaced;
    });
    next.push({
      vehicle: vehicle.id,
      event_car_class: Number(classId),
      division,
      judging: division === "display" ? false : judging,
      label: `${vehicle.year} ${vehicle.manufacture_name} ${vehicle.model}`,
      class_title: classes.find((item) => String(item.id) === String(classId))?.title || "",
    });
    const nextSpectators = spectatorHolding(cars.length, next.length, spectators);
    const saved = await saveDraft(next, tickets, nextSpectators);
    if (saved) {
      includedAnchor.current = includedSpectatorTickets(next.length);
      setSpectators(nextSpectators);
      setEditingVehicle("");
    }
    return saved;
  };

  const createCar = async (formEvent) => {
    formEvent.preventDefault();
    setError("");
    try {
      const { data } = await axios.post("/api/users/garage/", newCar, authHeaders(userInfo.token));
      setGarage((current) => [data, ...current]);
      setVehicleId(String(data.id));
      setAdding(false);
      setNewCar({ manufacture: "", vehicle_type: "automobile", year: "", model: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "The car could not be added.");
    }
  };

  const setTicketQty = (id, quantity) => {
    setTickets((current) => {
      const others = ticketCount(current) - (Number(current[id]) || 0);
      const allowed = Math.max(0, (Number(spectators) || 0) - others);
      return { ...current, [id]: Math.min(Math.max(0, quantity), allowed) };
    });
  };

  const registerAnother = async () => {
    setError("");
    const savedAlready = vehicleId && registeredIds.has(String(vehicleId));
    if (!vehicleId) {
      if (!cars.length) {
        setError("Choose a car.");
      }
      return;
    }
    if (!savedAlready && (stage < 2 || !classId)) {
      setStage(2);
      return;
    }
    if (classId) {
      const saved = await addCurrentCar();
      if (!saved) {
        return;
      }
    }
    setVehicleId("");
    setClassId("");
    setDivision("display");
    setJudging(false);
    setStage(1);
  };

  const next = async () => {
    setError("");
    if (stage === 1) {
      if (!vehicleId) {
        setError("Choose a car.");
        return;
      }
      const saved = await saveDraft(cars, capVipTickets(tickets, spectators), spectators);
      if (saved) {
        setStage(2);
      }
      return;
    }
    if (stage === 2) {
      const saved = await addCurrentCar();
      if (saved) {
        setStage(3);
      }
      return;
    }
    if (stage === 3) {
      if (purpose === "spectator" && Number(spectators) < 1) {
        setError("Add a spectator ticket to continue.");
        return;
      }
      if (purpose === "vip" && Number(spectators) < 1) {
        setError("Add a spectator ticket before a VIP luncheon ticket.");
        return;
      }
      const saved = await saveDraft(cars, capVipTickets(tickets, spectators), spectators);
      if (saved) {
        setStage(lunch ? 4 : 5);
      }
      return;
    }
    if (stage === 4) {
      const saved = await saveDraft(cars, capVipTickets(tickets, spectators), spectators);
      if (saved) {
        setStage(5);
      }
    }
  };

  const persistDraft = async () => {
    if (stage === 2 && vehicle && classId && !carTaken(vehicle.id)) {
      return addCurrentCar();
    }
    return saveDraft(cars, capVipTickets(tickets, spectators), spectators);
  };

  const hasVip = Boolean(lunch);
  const shownStage = !hasVip && stage === 4 ? 5 : stage;
  const steps = hasVip ? registrationSteps : registrationSteps.filter((step) => step.id !== 4);
  const carChosen = Boolean(vehicleId);
  const spectatorQty = Number(spectators) || 0;
  const stepReachable = (stepId) => {
    if (stepId === 1 || stepId === 3) {
      return true;
    }
    if (stepId === 2) {
      return carChosen;
    }
    if (stepId === 4) {
      return spectatorQty > 0;
    }
    if (stepId === 5) {
      return spectatorQty > 0 || cars.length > 0 || reached >= 5;
    }
    return false;
  };
  const stepName = (step) => {
    if (step.id === 2 && !carChosen) {
      return "Class. Choose a car first";
    }
    if (step.id === 4 && spectatorQty < 1) {
      return "VIP. Add a spectator ticket first";
    }
    if (step.id === 5 && spectatorQty < 1 && cars.length < 1 && reached < 5) {
      return "Receipt. Add a spectator ticket first";
    }
    return step.label;
  };

  return (
    <>
    <Modal show={open} onHide={onClose} centered className="entry-wizard">
      <Modal.Header closeButton>
        <Modal.Title>{dayTicketTitle(event?.date)}</Modal.Title>
        <nav className="entry-steps-nav" aria-label="Registration steps">
          {steps.map((step) => {
            const openStep = step.id !== shownStage && stepReachable(step.id);
            const place = step.id === shownStage ? "is-current" : openStep ? "is-done" : "is-ahead";
            const name = stepName(step);
            return (
              <button
                key={step.id}
                type="button"
                className={place}
                aria-current={step.id === shownStage ? "step" : undefined}
                aria-label={name}
                title={name}
                disabled={!openStep && step.id !== shownStage}
                onClick={async () => {
                  if (!openStep || saving) {
                    return;
                  }
                  setError("");
                  const saved = await persistDraft();
                  if (saved) {
                    setStage(step.id);
                  }
                }}
              >
                {step.label}
              </button>
            );
          })}
        </nav>
      </Modal.Header>
      <Modal.Body>
        {error ? <p className="text-danger">{error}</p> : null}
        {stage === 1 && (
          <>
            <p className="entry-ticket-note">
              Every registered car gets 2 tickets. The driver and 1 more. See more on the Spectators tab.
            </p>
            <p>Choose one car. The choice stays saved if you leave to talk it over.</p>
            {garage.length === 0 ? (
              <p className="quiet">No cars in the garage yet.</p>
            ) : (
              <div className="entry-cars" role="radiogroup" aria-label="Your cars">
                {garage.map((item) => {
                  const photo = item.images?.[0];
                  const selected = String(vehicleId) === String(item.id);
                  const taken = carTaken(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-disabled={taken || undefined}
                      disabled={taken}
                      className={taken ? "entry-car is-taken" : selected ? "entry-car is-selected" : "entry-car"}
                      onClick={() => {
                        if (!taken) setVehicleId(String(item.id));
                      }}
                    >
                      {photo ? (
                        <img className="entry-car-photo" src={photo.display || photo.thumb} alt="" />
                      ) : null}
                      <span className="entry-car-copy">
                        <strong>
                          {item.year} {item.manufacture_name} {item.model}
                        </strong>
                        {taken ? (
                          <span>Already registered</span>
                        ) : item.exterior_color ? (
                          <span>{item.exterior_color}</span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {adding && (
              <Form onSubmit={createCar} className="mt-2">
                <Form.Select
                  className="mb-2"
                  required
                  value={newCar.manufacture}
                  onChange={(input) => setNewCar({ ...newCar, manufacture: input.target.value })}
                >
                  <option value="">Make</option>
                  {makes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  className="mb-2"
                  value={newCar.vehicle_type}
                  onChange={(input) => setNewCar({ ...newCar, vehicle_type: input.target.value })}
                >
                  <option value="automobile">Automobile</option>
                  <option value="motorcycle">Motorcycle</option>
                </Form.Select>
                <Form.Control
                  className="mb-2"
                  required
                  placeholder="Year"
                  value={newCar.year}
                  onChange={(input) => setNewCar({ ...newCar, year: input.target.value })}
                />
                <Form.Control
                  className="mb-2"
                  required
                  placeholder="Model"
                  value={newCar.model}
                  onChange={(input) => setNewCar({ ...newCar, model: input.target.value })}
                />
                <Button type="submit" variant="primary">
                  Save car
                </Button>
              </Form>
            )}
          </>
        )}
        {stage === 2 && (
          <>
            <h2 className="entry-step-title">Choose a car class</h2>
            <div className="entry-choice-row">
              {entryOptions.length === 0 ? (
                <p className="field-note">This event has no types checked yet.</p>
              ) : (
                <div className="entry-type-pick">
                  <label htmlFor="entryType">Type</label>
                  <Form.Select
                    id="entryType"
                    value={entryOptions.some((item) => item.code === division) ? division : entryOptions[0].code}
                    onChange={(input) => {
                      const nextType = input.target.value;
                      setDivision(nextType);
                      if (nextType === "display") {
                        setJudging(false);
                      }
                    }}
                  >
                    {entryOptions.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.title}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}
              {division !== "display" && entryOptions.some((item) => item.code === division) ? (
                <button
                  type="button"
                  className={canJudge && judging ? "entry-judge is-yes" : "entry-judge is-no"}
                  disabled={!canJudge}
                  aria-pressed={canJudge && judging}
                  onClick={() => setJudging((current) => !current)}
                >
                  <strong>Want to be Judged</strong>
                  <span className="entry-judge-choice">
                    <span className="entry-judge-yes">Yes</span>
                    <span className="entry-judge-slash" aria-hidden="true">
                      /
                    </span>
                    <span className="entry-judge-no">No</span>
                  </span>
                </button>
              ) : null}
            </div>
            {fitting.length === 0 ? (
              <p className="quiet">No class on this event fits this car.</p>
            ) : (
              <div className="entry-classes" role="radiogroup" aria-label="Classes">
                {fitting.map((item) => {
                  const selected = String(classId) === String(item.id);
                  const logos = classMakes(item, makes);
                  return (
                    <article key={item.id} className={selected ? "entry-class is-selected" : "entry-class"}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className="entry-class-main"
                        onClick={() => setClassId(String(item.id))}
                      >
                        <strong>{item.title}</strong>
                        <span>{yearSpan(item)}</span>
                        {item.closed ? <span>This class is closed to new cars. This car can stay in it.</span> : null}
                      </button>
                      <div className="entry-class-logos">
                        {logos.length ? (
                          logos.map((make) =>
                            make.logo_url ? (
                              <img key={make.id} src={make.logo_url} alt={make.title} />
                            ) : (
                              <span key={make.id}>{make.title}</span>
                            )
                          )
                        ) : (
                          <span className="entry-class-any">Any make</span>
                        )}
                      </div>
                      <div className="entry-class-end">
                        <button
                          type="button"
                          className="entry-class-more"
                          aria-label={`More about ${item.title}`}
                          title={`More about ${item.title}`}
                          onClick={() => setOpenClass(item)}
                        >
                          More
                        </button>
                        <span className="entry-class-split" aria-hidden="true">
                          |
                        </span>
                        <button
                          type="button"
                          className="entry-class-select"
                          aria-pressed={selected}
                          onClick={() => setClassId(String(item.id))}
                        >
                          Select
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
        {stage === 3 && (
          <>
            <h2 className="entry-step-title">Spectator tickets</h2>
            {purpose === "vip" ? (
              <p className="spectator-lead">
                A VIP ticket needs a spectator ticket. Add it here, then choose the luncheon.
              </p>
            ) : (
              <p className="spectator-lead">
                A registered car comes with 2 spectator tickets: one for the driver and one more. Extra
                tickets are full price.
              </p>
            )}
            <div className="admit-list">
              <article className="admit-ticket">
                <div className="admit-stub">
                  <span className="admit-mark">Show</span>
                  <StubDate value={event.date} />
                </div>
                <div className="admit-main">
                  <p className="site-kicker">Spectator admission</p>
                  <h3>{event.title}</h3>
                  <div className="admit-fare">
                    <div>
                      <strong>Spectator ticket</strong>
                      <span>
                        {Number(event.spectator_fee) > 0
                          ? money(event.spectator_fee)
                          : "Price will be posted here."}
                        {freeNote ? <span className="ticket-free-note"> {freeNote}</span> : null}
                      </span>
                    </div>
                    <div className="admit-qty">
                      <button
                        type="button"
                        aria-label={
                          includedTickets > 0 && Number(spectators) <= includedTickets
                            ? "Included tickets stay with the registered cars"
                            : "Fewer spectator tickets"
                        }
                        title={
                          includedTickets > 0 && Number(spectators) <= includedTickets
                            ? "Included tickets stay with the registered cars"
                            : "Fewer spectator tickets"
                        }
                        disabled={Number(spectators) <= includedTickets}
                        onClick={() =>
                          setSpectators((current) => Math.max(includedTickets, Number(current) - 1))
                        }
                      >
                        −
                      </button>
                      <span>{Number(spectators) || 0}</span>
                      <button
                        type="button"
                        aria-label="More spectator tickets"
                        title="More spectator tickets"
                        onClick={() => setSpectators((current) => Number(current) + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </>
        )}
        {shownStage === 4 && (
          <>
            <h2 className="entry-step-title">Choose your tickets</h2>
            {!lunch ? (
              <p>This event has no VIP luncheon.</p>
            ) : (lunch.ticket_types || []).length === 0 ? (
              <p>Tickets for this luncheon will be posted here.</p>
            ) : (
              <>
                <p className="spectator-lead">
                  {Number(spectators) < 1
                    ? "Add a spectator ticket before a VIP luncheon ticket."
                    : `A VIP luncheon ticket needs a spectator ticket. You can add ${Number(spectators)} VIP ${
                        Number(spectators) === 1 ? "ticket" : "tickets"
                      }.`}
                </p>
                <div className="admit-list">
                  {lunch.ticket_types.map((ticket) => {
                    const quantity = Number(tickets[ticket.id] || 0);
                    const vipHeld = ticketCount(tickets);
                    const spectatorQty = Number(spectators) || 0;
                    const moreLabel =
                      spectatorQty < 1
                        ? "Add a spectator ticket before a VIP ticket"
                        : vipHeld >= spectatorQty
                          ? "VIP tickets match the spectator tickets"
                          : `More ${ticket.name} tickets`;
                    return (
                      <article key={ticket.id} className="admit-ticket">
                        <div className="admit-stub">
                          {lunch.logo_url ? (
                            <img className="admit-logo" src={lunch.logo_url} alt="" />
                          ) : (
                            <span className="admit-mark">VIP</span>
                          )}
                          <StubDate value={lunch.event_date} />
                          {lunch.menu_url ? (
                            <MenuLink href={lunch.menu_url} />
                          ) : null}
                        </div>
                        <div className="admit-main">
                          <p className="site-kicker">VIP luncheon</p>
                          <h3>{lunch.title}</h3>
                          {lunch.hosted ? <p className="admit-host">Hosted by {lunch.hosted}</p> : null}
                          <div className="admit-fare">
                            <div>
                              <strong>{ticket.name}</strong>
                              <span>{money(ticket.price)}</span>
                            </div>
                            <div className="admit-qty">
                              <button
                                type="button"
                                aria-label={`Fewer ${ticket.name} tickets`}
                                title={`Fewer ${ticket.name} tickets`}
                                disabled={quantity <= 0}
                                onClick={() => setTicketQty(ticket.id, quantity - 1)}
                              >
                                −
                              </button>
                              <span>{quantity}</span>
                              <button
                                type="button"
                                aria-label={moreLabel}
                                title={moreLabel}
                                disabled={spectatorQty < 1 || vipHeld >= spectatorQty}
                                onClick={() => setTicketQty(ticket.id, quantity + 1)}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
        {shownStage === 5 && (
          <EntryReceipt
            event={event}
            userName={userInfo?.name}
            userEmail={userInfo?.email}
            cars={cars}
            garage={garage}
            tickets={tickets}
            spectators={spectators}
            lunch={lunch}
            onAddCar={() => {
              setEditingVehicle("");
              setVehicleId("");
              setClassId("");
              setStage(1);
            }}
            onEdit={(item) => {
              setError("");
              setEditingVehicle(String(item.vehicle));
              setVehicleId(String(item.vehicle));
              setClassId(String(item.event_car_class || ""));
              setDivision(item.division || "display");
              setJudging(Boolean(item.judging));
              setStage(1);
            }}
            onTicketQty={async (id, quantity) => {
              const others = ticketCount(tickets) - (Number(tickets[id]) || 0);
              const allowed = Math.max(0, (Number(spectators) || 0) - others);
              const next = { ...tickets, [id]: Math.min(Math.max(0, quantity), allowed) };
              const saved = await saveDraft(cars, next, spectators);
              if (!saved) {
                return;
              }
              if (registrationCleared(cars, next, spectators)) {
                forgetSavedEntry();
                return;
              }
              setTickets(next);
            }}
            onSpectatorQty={async (quantity) => {
              const next = Math.max(includedTickets, quantity);
              const nextTickets = capVipTickets(tickets, next);
              const saved = await saveDraft(cars, nextTickets, next);
              if (!saved) {
                return;
              }
              if (registrationCleared(cars, nextTickets, next)) {
                forgetSavedEntry();
                return;
              }
              includedAnchor.current = includedTickets;
              setTickets(nextTickets);
              setSpectators(next);
            }}
            onRemove={async (item) => {
              const name = item.label || item.class_title || "this car";
              if (!(await ask(removePrompt(name)))) {
                return;
              }
              const next = cars.filter((car) => String(car.vehicle) !== String(item.vehicle));
              const nextSpectators = spectatorHolding(cars.length, next.length, spectators);
              const nextTickets = capVipTickets(tickets, nextSpectators);
              const saved = await saveDraft(next, nextTickets, nextSpectators);
              if (!saved) {
                return;
              }
              if (registrationCleared(next, nextTickets, nextSpectators)) {
                forgetSavedEntry();
                return;
              }
              includedAnchor.current = includedSpectatorTickets(next.length);
              setTickets(nextTickets);
              setSpectators(nextSpectators);
              if (String(vehicleId) === String(item.vehicle) || String(editingVehicle) === String(item.vehicle)) {
                setEditingVehicle("");
                setVehicleId("");
                setClassId("");
                setDivision("display");
                setJudging(false);
              }
            }}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {shownStage === 1 && (
          <button type="button" className="entry-add-garage" onClick={() => setAdding((current) => !current)}>
            Add a new car to your garage
          </button>
        )}
        {shownStage > 1 && (
          <button
            type="button"
            className="btn btn-site-quiet"
            onClick={() => setStage(shownStage === 5 && !hasVip ? 3 : shownStage - 1)}
          >
            Back
          </button>
        )}
        {shownStage === 2 && (
          <button type="button" className="btn btn-register-another" onClick={registerAnother} disabled={saving}>
            Register another car
          </button>
        )}
        {shownStage < 5 ? (
          <button type="button" className="btn btn-site" onClick={next} disabled={saving}>
            Next
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-site-quiet" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn-purchase"
              disabled={
                saving ||
                (cars.length === 0 &&
                  Number(spectators) < 1 &&
                  !Object.values(tickets).some((quantity) => Number(quantity) > 0))
              }
              onClick={async () => {
                const saved = await saveDraft(cars, capVipTickets(tickets, spectators), spectators);
                if (!saved) {
                  return;
                }
                onClose();
                navigate("/cart");
              }}
            >
              Add to cart
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
    <Modal
      show={Boolean(openClass)}
      onHide={() => setOpenClass(null)}
      centered
      className="class-makes-modal"
      backdropClassName="class-makes-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>{openClass?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {openClass ? (
          <>
            <p className="class-limit-lead">{classLimitSentence(openClass)}</p>
            <dl className="class-detail-facts">
              <div>
                <dt>Type</dt>
                <dd>{typeLimit(openClass.vehicle_type)}</dd>
              </div>
              <div>
                <dt>Years</dt>
                <dd>{yearLimit(openClass)}</dd>
              </div>
              <div>
                <dt>Makes</dt>
                <dd>{openClass.manufacture_ids?.length ? "Only the makes below" : "Any make"}</dd>
              </div>
            </dl>
            {classMakes(openClass, makes).length ? (
              <div className="class-logo-grid">
                {classMakes(openClass, makes).map((make) => (
                  <figure key={make.id}>
                    {make.logo_url ? <img src={make.logo_url} alt="" /> : null}
                    <figcaption>{make.title}</figcaption>
                  </figure>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </Modal.Body>
    </Modal>
    {confirmDialog}
    </>
  );
}

export default EntryWizard;
